import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Loader2, Check, X, Sparkles, Download, RotateCw } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, getDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

export const MonthlyCalendarTab = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [fridays, setFridays] = useState<Date[]>([]);
  const [giraNames, setGiraNames] = useState<Record<string, string>>({});
  const [additionalInfo, setAdditionalInfo] = useState<Record<string, string>>({});
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const imagePreviewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadFridays();
    loadExistingGiras();
  }, [currentMonth]);

  const loadFridays = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const allDays = eachDayOfInterval({ start, end });
    const fridaysInMonth = allDays.filter(date => getDay(date) === 5); // 5 = Friday
    setFridays(fridaysInMonth);
  };

  const loadExistingGiras = async () => {
    const monthYear = format(currentMonth, 'yyyy-MM');
    const { data } = await supabase
      .from('monthly_giras')
      .select('*')
      .eq('month_year', monthYear);

    if (data) {
      const names: Record<string, string> = {};
      const infos: Record<string, string> = {};
      data.forEach(gira => {
        names[gira.gira_date] = gira.gira_name;
        if (gira.additional_info) {
          infos[gira.gira_date] = gira.additional_info;
        }
      });
      setGiraNames(names);
      setAdditionalInfo(infos);
    }
  };

  const handleGiraNameChange = (date: Date, name: string) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setGiraNames(prev => ({ ...prev, [dateStr]: name }));
  };

  const handleAdditionalInfoChange = (date: Date, info: string) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setAdditionalInfo(prev => ({ ...prev, [dateStr]: info }));
  };

  const handleRegenerateCalendar = async () => {
    // Limpar o preview atual
    setGeneratedImage(null);
    
    // Rolar para o topo da página
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Pequeno delay para garantir que o scroll aconteça
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Gerar nova imagem
    await handleGenerateCalendar();
  };

  const handleGenerateCalendar = async () => {
    setIsGenerating(true);
    
    // Rolar para o topo e bloquear interação
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      const monthYear = format(currentMonth, 'yyyy-MM');
      
      // Save giras to database first
      for (const friday of fridays) {
        const dateStr = format(friday, 'yyyy-MM-dd');
        const giraName = giraNames[dateStr];
        const info = additionalInfo[dateStr];
        
        if (giraName) {
          await supabase
            .from('monthly_giras')
            .upsert({
              month_year: monthYear,
              gira_date: dateStr,
              gira_name: giraName,
              additional_info: info || null,
            }, {
              onConflict: 'month_year,gira_date'
            });
        }
      }

      // Generate calendar image - include ALL Fridays
      const girasArray = fridays.map(f => {
        const dateStr = format(f, 'yyyy-MM-dd');
        return {
          gira_date: dateStr,
          gira_name: giraNames[dateStr] || 'Não teremos gira',
          additional_info: additionalInfo[dateStr] || null
        };
      });

      const { data, error } = await supabase.functions.invoke('generate-monthly-calendar', {
        body: { monthYear, giras: girasArray }
      });

      if (error) throw error;

      setGeneratedImage(data.imageUrl);
      
      // Save to monthly_calendar_images
      await supabase
        .from('monthly_calendar_images')
        .upsert({
          month_year: monthYear,
          image_url: data.imageUrl,
          status: 'pending'
        }, {
          onConflict: 'month_year'
        });

      toast({
        title: "Calendário gerado!",
        description: "Revise a imagem e aprove ou rejeite.",
      });

      // Scroll automático para a imagem
      setTimeout(() => {
        imagePreviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error: any) {
      toast({
        title: "Erro ao gerar calendário",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async () => {
    setIsSaving(true);
    try {
      const monthYear = format(currentMonth, 'yyyy-MM');
      
      // Update status to approved
      await supabase
        .from('monthly_calendar_images')
        .update({ status: 'approved' })
        .eq('month_year', monthYear);

      // Update current_gira_images for agenda_mes
      const { data: calendarData } = await supabase
        .from('monthly_calendar_images')
        .select('image_url')
        .eq('month_year', monthYear)
        .single();

      if (calendarData) {
        await supabase
          .from('current_gira_images')
          .update({ image_url: calendarData.image_url })
          .eq('image_type', 'agenda_mes');
      }

      toast({
        title: "Calendário aprovado!",
        description: "Redirecionando para a página inicial...",
      });

      setGeneratedImage(null);
      
      // Redirecionar para página inicial no topo
      setTimeout(() => {
        navigate('/');
        window.scrollTo(0, 0);
      }, 500);
    } catch (error: any) {
      toast({
        title: "Erro ao aprovar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    const monthYear = format(currentMonth, 'yyyy-MM');
    await supabase
      .from('monthly_calendar_images')
      .update({ status: 'rejected' })
      .eq('month_year', monthYear);

    setGeneratedImage(null);
    toast({
      title: "Calendário rejeitado",
      description: "Você pode gerar uma nova versão.",
    });
  };

  const handleDownloadImage = async () => {
    if (!generatedImage) return;
    try {
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const monthName = format(currentMonth, 'MMMM-yyyy', { locale: ptBR });
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `calendario-${monthName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);

      toast({
        title: "Download concluído",
        description: "A imagem foi salva com sucesso.",
      });
    } catch (error) {
      console.error('Erro no download da imagem:', error);
      window.open(generatedImage, '_blank');
      toast({
        title: "Download alternativo",
        description: "A imagem foi aberta em outra aba. Em celulares, pressione e segure para salvar.",
      });
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* Loading Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm">
          <Loader2 className="w-16 h-16 animate-spin text-primary mb-4" />
          <p className="text-xl font-medium text-foreground">Imagem sendo gerada, aguarde...</p>
        </div>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Giras do Mês
          </CardTitle>
          <CardDescription>
            {format(currentMonth, "MMMM 'de' yyyy", { locale: ptBR })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 mb-4">
            <Button
              variant="outline"
              onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
            >
              Mês Anterior
            </Button>
            <Button
              variant="outline"
              onClick={() => setCurrentMonth(new Date())}
            >
              Mês Atual
            </Button>
            <Button
              variant="outline"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              Próximo Mês
            </Button>
          </div>

          <div className="space-y-4">
            {fridays.map((friday) => {
              const dateStr = format(friday, 'yyyy-MM-dd');
              return (
                <div key={dateStr} className="space-y-2 p-3 border rounded-lg">
                  <Label className="text-sm font-medium">
                    {format(friday, "dd 'de' MMMM", { locale: ptBR })}
                  </Label>
                  <Input
                    placeholder="Nome da Gira (ex: Pretos-Velhos) ou deixe vazio para 'Não teremos gira'"
                    value={giraNames[dateStr] || ''}
                    onChange={(e) => handleGiraNameChange(friday, e.target.value)}
                  />
                  <div className="space-y-1">
                    <Input
                      placeholder="Informação adicional (ex: Sem atendimento)"
                      value={additionalInfo[dateStr] || ''}
                      onChange={(e) => handleAdditionalInfoChange(friday, e.target.value)}
                      maxLength={50}
                      className="text-sm"
                    />
                    <p className={`text-xs text-right ${(additionalInfo[dateStr]?.length || 0) > 45 ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {additionalInfo[dateStr]?.length || 0}/50 caracteres
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            onClick={handleGenerateCalendar}
            disabled={isGenerating}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Gerando Calendário com IA...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Gerar Calendário do Mês
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {generatedImage && (
        <Card ref={imagePreviewRef}>
          <CardHeader>
            <CardTitle>Preview do Calendário</CardTitle>
            <CardDescription>
              Revise a imagem gerada e aprove ou rejeite
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative w-full overflow-hidden rounded-lg border">
              <img
                src={generatedImage}
                alt="Calendário Gerado"
                className="w-full h-auto"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleDownloadImage}
                variant="outline"
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Imagem
              </Button>
                <Button
                  onClick={handleRegenerateCalendar}
                  disabled={isGenerating}
                  variant="outline"
                  className="flex-1"
                >
                  <RotateCw className="w-4 h-4 mr-2" />
                  Gerar Novamente
                </Button>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleApprove}
                disabled={isSaving}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Aprovar e Publicar
              </Button>
              <Button
                onClick={handleReject}
                disabled={isSaving}
                variant="destructive"
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Rejeitar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};